import { useEffect, useEffectEvent, useState, type KeyboardEvent } from 'react'
import { pushEscape } from '../layer'
import {
  firstSelectable,
  isSelectable,
  lastSelectable,
  stepSelectable,
  type ListboxModel,
  type ListboxOption
} from './model'

const PAGE = 10

export interface UseListboxOptions {
  model: ListboxModel
  /** Row index of the selected option, activated when opening by keyboard. */
  selectedIndex: number
  /** With nothing active, activate the first selectable row (while typing). */
  isAutoActive: boolean
  onPick: (option: ListboxOption) => void
  /** Multi-select: picking toggles and keeps the popup open. */
  isMulti?: boolean
}

export interface ListboxController {
  isOpen: boolean
  /** Active row index, -1 for none. */
  activeIndex: number
  open: (start?: 'none' | 'first' | 'last') => void
  close: () => void
  setActiveIndex: (index: number) => void
  /** Pick an option (Enter or click); closes unless multi-select. */
  pick: (option: ListboxOption) => void
  /** Clear the active option so typing re-activates the first match. */
  resetActive: () => void
  /** Arrow keys, Page Up/Down, Home/End, Enter and Tab. Escape goes through the Escape stack. */
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void
}

/**
 * Open state and the active option for a combobox listbox (WAI-ARIA 1.2).
 * The active option is tracked by value, so it survives filtering and
 * windowing; focus stays in the input (aria-activedescendant).
 */
export function useListbox({
  model,
  selectedIndex,
  isAutoActive,
  onPick,
  isMulti = false
}: UseListboxOptions): ListboxController {
  const { rows, indexOf } = model
  const [isOpen, setIsOpen] = useState(false)
  const [activeValue, setActiveValue] = useState<string | null>(null)

  const tracked = activeValue === null ? -1 : (indexOf.get(activeValue) ?? -1)
  const activeIndex = tracked === -1 && isAutoActive && isOpen ? firstSelectable(rows) : tracked

  const setActiveIndex = (index: number) => {
    const row = rows[index]
    if (row?.kind === 'option') setActiveValue(row.option.value)
  }

  const open = (start: 'none' | 'first' | 'last' = 'none') => {
    setIsOpen(true)
    if (start === 'none') {
      setActiveIndex(selectedIndex)
      return
    }
    setActiveIndex(
      isSelectable(rows[selectedIndex])
        ? selectedIndex
        : start === 'first'
          ? firstSelectable(rows)
          : lastSelectable(rows)
    )
  }

  const close = () => {
    setIsOpen(false)
    setActiveValue(null)
  }

  const pick = (option: ListboxOption) => {
    if (option.disabled) return
    onPick(option)
    if (!isMulti) close()
  }

  const onEscape = useEffectEvent(() => {
    close()
  })

  useEffect(() => {
    if (!isOpen) return
    return pushEscape(() => {
      onEscape()
      return true
    })
  }, [isOpen])

  const move = (step: number) => {
    if (activeIndex === -1) setActiveIndex(step > 0 ? firstSelectable(rows) : lastSelectable(rows))
    else setActiveIndex(stepSelectable(rows, activeIndex, step))
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault()
        const down = event.key === 'ArrowDown'
        if (!isOpen) open(down ? 'first' : 'last')
        else if (event.altKey && !down) close()
        else move(down ? 1 : -1)
        return
      }
      case 'PageDown':
      case 'PageUp':
        if (!isOpen) return
        event.preventDefault()
        move(event.key === 'PageDown' ? PAGE : -PAGE)
        return
      case 'Home':
      case 'End':
        if (!isOpen) return
        event.preventDefault()
        setActiveIndex(event.key === 'Home' ? firstSelectable(rows) : lastSelectable(rows))
        return
      case 'Enter': {
        if (!isOpen) return
        event.preventDefault()
        const row = rows[activeIndex]
        if (isSelectable(row)) pick(row.option)
        return
      }
      case 'Tab':
        if (isOpen) close()
        return
    }
  }

  return {
    isOpen,
    activeIndex,
    open,
    close,
    setActiveIndex,
    pick,
    resetActive: () => {
      setActiveValue(null)
    },
    onKeyDown
  }
}
